import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit } from '@angular/core';
import { ChartDataset, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { DeploymentStatisticsOverTime } from '../../models/backend/deployment/get-executions';
import 'chartjs-adapter-date-fns';
import { Utils } from '../../shared/utils';

@Component({
  selector: 'app-deployment-statistics-over-time',
  standalone: true,
  imports: [BaseChartDirective],
  templateUrl: './deployment-statistics-over-time.component.html',
  styleUrl: 'deployment-statistics-over-time.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DeploymentStatisticsOverTimeComponent implements OnInit {
  @Input() statisticsOverTime?: DeploymentStatisticsOverTime;

  readonly _chartOptions: ChartOptions = {
    responsive: true,
    scales: {
      x: {
        type: 'time',
        time: {
          minUnit: 'hour',
          displayFormats: {
            hour: 'MM/dd HH:mm',
          },
          tooltipFormat: 'y/MM/dd HH:mm',
        },
        title: {
          display: true,
          text: 'Time',
        },
      },
      devices: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Count',
        },
      },
      averageDuration: {
        beginAtZero: true,
        position: 'right',
        title: {
          display: true,
          text: 'Average Duration',
        },
        ticks: {
          callback: function (val) {
            return Utils.humanizeDuration(+val * 1000, 'hours');
          },
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
    plugins: {
      decimation: {
        enabled: true,
        algorithm: 'min-max',
      },
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          boxHeight: 0,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const dataset = context.dataset;
            const label = dataset.label || '';
            let value: string | number = context.parsed.y;

            if (dataset.yAxisID === 'averageDuration') {
              value = Utils.humanizeDuration(context.parsed.y * 1000, 'minutes');
            }

            return `${label}: ${value}`;
          },
        },
      },
    },
    animation: {
      duration: 300,
    },
  };

  _datasets: ChartDataset[] = [];

  ngOnInit(): void {
    if (!this.statisticsOverTime) {
      return;
    }

    this._datasets = [
      {
        label: 'Notified',
        data: this.statisticsOverTime.map((item) => ({
          x: item.timestamp,
          y: item.numberOfQueuedThings ?? 0,
        })),
        yAxisID: 'devices',
        pointBackgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        fill: false,
        hidden: true,
      },
      {
        label: 'Succeeded',
        data: this.statisticsOverTime.map((item) => ({
          x: item.timestamp,
          y: item.numberOfSucceededThings ?? 0,
        })),
        yAxisID: 'devices',
        pointBackgroundColor: '#198754',
        backgroundColor: '#19875410',
        borderColor: '#198754',
        fill: true,
      },
      {
        label: 'Failed',
        data: this.statisticsOverTime.map((item) => ({
          x: item.timestamp,
          y: item.numberOfFailedThings ?? 0,
        })),
        yAxisID: 'devices',
        pointBackgroundColor: '#dc3545',
        borderColor: '#dc3545',
        fill: false,
      },
      {
        label: 'In Progress',
        data: this.statisticsOverTime.map((item) => ({
          x: item.timestamp,
          y: item.numberOfInProgressThings ?? 0,
        })),
        yAxisID: 'devices',
        pointBackgroundColor: '#ffc107',
        borderColor: '#ffc107',
        fill: false,
      },
      {
        label: 'Canceled',
        data: this.statisticsOverTime.map((item) => ({
          x: item.timestamp,
          y: item.numberOfCanceledThings ?? 0,
        })),
        yAxisID: 'devices',
        pointBackgroundColor: '#6c757d',
        borderColor: '#6c757d',
        fill: false,
      },
      {
        label: 'Rejected',
        data: this.statisticsOverTime.map((item) => ({
          x: item.timestamp,
          y: item.numberOfRejectedThings ?? 0,
        })),
        yAxisID: 'devices',
        pointBackgroundColor: '#FF6B57',
        borderColor: '#FF6B57',
        fill: false,
      },
      {
        label: 'Timed Out',
        data: this.statisticsOverTime.map((item) => ({
          x: item.timestamp,
          y: item.numberOfTimedOutThings ?? 0,
        })),
        yAxisID: 'devices',
        pointBackgroundColor: '#561435',
        borderColor: '#561435',
        fill: false,
      },
      {
        label: 'Notified-to-Done Duration',
        data: this.statisticsOverTime
          .filter((item) => !!item.shadowToDoneDurationAverage)
          .map((item) => ({
            x: item.timestamp,
            y: Math.round((item.shadowToDoneDurationAverage ?? 0) / 1000), // Convert ms to seconds
          })),
        yAxisID: 'averageDuration',
        pointBackgroundColor: '#8540f5',
        borderColor: '#8540f5',
        fill: false,
      },
      {
        label: 'Presign-to-Done Duration',
        data: this.statisticsOverTime
          .filter((item) => !!item.presignToDoneDurationAverage)
          .map((item) => ({
            x: item.timestamp,
            y: Math.round((item.presignToDoneDurationAverage ?? 0) / 1000), // Convert ms to seconds
          })),
        yAxisID: 'averageDuration',
        pointBackgroundColor: '#20c997',
        borderColor: '#20c997',
        fill: false,
      },
    ];
  }
}
